import { fetchLinkedToken } from "@endpoints/auth.service";
import { loginUser } from "@store/actions/auth.action";

import {
  createBrowserRouter,
  Navigate,
  redirect,
  RouteObject,
} from "react-router-dom";
import { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useAxiosInstance } from "@utils/setupInterceptor";
import {
  EnumUserTypes,
  RecruiterApprovalStatus,
  RecruiterTypes,
  userTypes,
} from "@utils/constants";

import Layout from "@pages/Layout/Layout";
import LandingPage from "@pages/LandingPage/LandingPage";
import LoginPage from "@pages/LoginPage/LoginPage";
import RecruiterLoginPage from "@pages/LoginPage/RecruiterLoginPage";
import RegisterPage from "@pages/RegisterPage/ResgisterPage";
import EmailVerification from "@pages/EmailVerification/EmailVerification";
import MyAssessmentsPage from "@pages/MyAssessmentsPage/MyAssessmentsPage";
import CreateAssessment from "@pages/MyAssessmentsPage/CreateAssessmentSection/CreateAssessmentSection";
import MyAssessmentsDetailsPage from "@pages/MyAssessmentsDetailsPage/MyAssessmentsDetailsPage";
import CandidateAssessments from "@pages/MyAssessmentsPage/CandidateAssessments/CandidateAssessments";
import AnswerAssessment from "@pages/MyAssessmentsPage/AnswerAssessment/AnswerAssessment";
import Profile from "@pages/ProfilePage/ProfilePage";
import AssessmentListDetails from "@pages/MyAssessmentsPage/AssessmentListDetails/AssessmentListDetails";
import AssessmentView from "@pages/MyAssessmentsPage/AssessmentView/AssessmentView";
import CreateProfile from "@pages/CreateProfile/CreateProfile";
import JobsListPage from "@pages/JobsListPage/JobsListPage";
import VerifyUserJobPage from "@pages/VerifyUserJobPage/VerifyUserJobPage";
import PublicJobDetailPage from "@pages/PublicJobDetailPage/PublicJobDetailPage";
import UnauthorizedPage from "@pages/UnauthorizedPage/UnauthorizedPage";
import ChangePasswordPage from "@pages/ChangePasswordPage/ChangePasswordPage";
import AccountMembers from "@pages/AccountMembersPage/AccountMembers";
import AccountMembersDetail from "@pages/AccountMembersPage/AccountMembersDetail/AccountMembersDetail";
import ViewAgentProfile from "@pages/ViewAgentProfile/ViewAgentProfile";
import AccountPage from "@pages/AccountPage/AccountPage";
import MyAccountDetailsPage from "@pages/MyAccountDetailsPage/MyAccountDetailsPage";
import MyCompanyDetailsPage from "@pages/MyCompanyDetailsPage/MyCompanyDetailsPage";
import RecruiterCreateProfilePage from "@pages/RecruiterCreateProfilePage/RecruiterCreateProfilePage";
import PublicProfilePage from "@pages/PublicProfilePage/PublicProfilePage";
import SearchLayout from "@pages/SearchPage/SearchLayout";
import SearchPage from "@pages/SearchPage/SearchPage";
import PossibleMatchPage from "@pages/SearchPage/PossibleMatchPage";
import RequestedDetailsPage from "@pages/SearchPage/RequestedDetailsPage";
import LoginAmbassadorPage from "@pages/Ambassador/Login/Login";
import RegisterAmbassadorPage from "@pages/Ambassador/Register/Register";
import NetworkDetailPage from "@pages/Ambassador/Network/Detail/Detail";
import MatchesPage from "@pages/MatchesPage/MatchesPage";
import Matches2Page from "@pages/Matches2Page/Matches2Page";
import MatchDetailPage from "@pages/MatchDetailPage/MatchDetailPage";
import DetailPage from "@pages/SearchPage/DetailPage";
import TemplateResultPage from "@pages/SearchPage/TemplateResultPage";
import TemplateSearchesPage from "@pages/SearchPage/TemplateSearchesPage";
import ForgotPassword from "@pages/ForgotPassword/ForgotPassword";
import UpdateForgotPassword from "@pages/UpdateForgotPassword/UpdateForgotPassword";
import GenerateUserNamePage from "@pages/GenerateUserNamePage/GenerateUserNamePage";
import JobApplyPage from "@pages/JobApplyPage/JobApplyPage";
import JobApplicantDetail from "@pages/JobPage/JobApplicantDetail/JobApplicantDetail";
import { isNullOrEmpty } from "@utils/helperFunction";
import AddReferralPage from "@pages/AddReferralPage/AddReferralPage";
import SettingsPage from "@pages/SettingsPage/SettingsPage";
import MatchesDetailsPage from "@pages/MatchesPage/MatchesDetailsPage";
import PaymentMethodPage from "@pages/Stripe/PaymentMethodPage";
import BalancePage from "@pages/Stripe/BalancePage";
import NotificationPage from "@pages/NotificationPage/NotificationPage";
import { getPaymentMethods } from "@endpoints/stripe.service";
import { setSearchData } from "@store/actions/search.action";
import { setPaymentMethods } from "@store/actions/payment.action";
import {
  fetchJobMessagesProfile,
  fetchUnseenMessageCount,
} from "@store/actions/message.action";
import TransactionPage from "@pages/TransactionPage/TransactionPage";
import TransactionDetailPage from "@pages/TransactionPage/TransactionDetailPage";
import ReferralMemberPage from "@pages/ReferralMemberPage/ReferralMemberPage";
import JobApplicationDetail from "@pages/Ambassador/Network/JobApplicationDetail/JobApplicationDetail";
import { RecruiterCheckoutPage } from "@pages/Stripe/RecruiterCheckoutPage";
import AddReferralAccount from "@pages/AddReferralAccount/AddReferralAccount";
import Privacy from "@pages/SettingsPage/privacy/privacy";
import HiringCompanyLoginPage from "@pages/LoginPage/hiringCompany";
import HiringCompanyRegisterPage from "@pages/LoginPage/hiringCompanyRegister";
import CounterProposalPollPage from "@pages/CounterProposalPollPage/CounterProposalPollPage";
import TransactionUserDetailPage from "@pages/TransactionPage/TransactionUserDetailPage";
import store from "@store/index";
import { RecruiterRoutes } from "./RecruiterRoutes";
import { HiringCompanyRoutes } from "./HiringCompanyRoutes";
import { CandidateRoutes } from "./CandidateRoutes";
import MeetingDetailPage from "@pages/MeetingDetailPage/MeetingDetailPage";
import { ReferralAmbassadorRoutes } from "./ReferralAmbassadorRoutes";
import DashboardPage from "@pages/Dashboard/Dashboard";
import MessagePage from "@pages/MessagesPage/MessagesPage";
import { HomeRedirect } from "./HomeRedirect";
import MicrosoftCallback from "@pages/Auth/MicrosoftCallback";
import SsoLogin from "@pages/Auth/SsoLogin";
import RecruiterRegisterPage from "@pages/RegisterPage/RecruiterRegisterPage";
import ProfilePage2 from "@pages/ProfilePage/ProfilePage2";
import ProfileOriginal from "@pages/ProfilePage/ProfilePageOriginal";
import CreateBasicProfileLayout from "@pages/CreateBasicProfilePage/CreateBasicProfilePageLayout";
import CreateProfileNew from "@pages/CreateProfileNew/CreateProfileNew";
import ProfilePage3 from "@pages/ProfilePage/ProfilePage3";

export function routerLoaders() {
  const loginLoader = () => {
    const { authenticated, loading } = store.getState().auth;

    if (loading) return null;

    if (authenticated) {
      return redirect("/");
    }

    return null;
  };

  const authLoader = async () => {
    const { authenticated, loading } = store.getState().auth;

    if (loading) return null;

    if (!authenticated) {
      const ssoEmail = localStorage.getItem("ssoEmail");
      if (ssoEmail) {
        return redirect("/sso-login");
      }

      const accessToken = localStorage.getItem("accessToken");
      if (accessToken) {
        try {
          const response = await fetchLinkedToken();
          const payload = response?.data || response;
          if (payload?.user) {
            store.dispatch(loginUser(payload));
            return null;
          }
        } catch (error) {
          // fall through to /login
        }

        localStorage.removeItem("accessToken");
        localStorage.removeItem("refreshToken");
        localStorage.removeItem("sessionId");
      }

      return redirect("/login");
    }

    return null;
  };

  const tokenAwareLoader = (...args: Parameters<typeof authLoader>) => {
    const accessToken = localStorage.getItem("accessToken");
    return accessToken ? authLoader(...args) : loginLoader();
  };

  return { loginLoader, authLoader, tokenAwareLoader };
}

const { authLoader, loginLoader, tokenAwareLoader } = routerLoaders();

function useRoutes() {
  const dispatch = useDispatch();
  const auth = useSelector((state: any) => state.auth);
  // const { loginLoader, authLoader } = useAuthLoader();
  useAxiosInstance();

  const isLoggedIn =
    (auth.authenticated && auth.user && auth.user._id) ||
    auth?.user?.userType === userTypes.REFERRAL_AMBASSADOR;

  const userNameNotExist = isLoggedIn && isNullOrEmpty(auth?.user?.userName);

  const isRecruiter = auth?.user?.userType === userTypes.RECRUITER;
  const isCandidate = auth?.user?.userType === userTypes.CANDIDATE;
  const isReferrealAmbassador =
    auth?.user?.userType === userTypes.REFERRAL_AMBASSADOR;
  const isRecruiterAdmin =
    isRecruiter && auth?.user?.recruiterType === RecruiterTypes?.ADMIN;
  const isHiringCompany = auth?.user?.userType === userTypes.HIRING_COMPANY;

  let layoutChildren: RouteObject[] = [
    // {
    //   path: "/",
    //   element: isLoggedIn ? (
    //     isRecruiterAdmin &&
    //     (!auth?.user?.recruiterApprovalStatus ||
    //       auth?.user?.recruiterApprovalStatus ===
    //         RecruiterApprovalStatus.REQUESTED) ? (
    //       <Navigate to="/create-profile" />
    //     ) : userNameNotExist ? (
    //       <Navigate to="/generate-user-name" />
    //     ) : isCandidate && auth?.user?.enableDefaultProfileOption ? (
    //       // Candidate
    //       <Navigate to="/create-profile" />
    //     ) : isCandidate || isRecruiter || isReferrealAmbassador ? (
    //       <Navigate to="/dashboard" />
    //     ) : isHiringCompany ? (
    //       <Navigate to="/requirements" />
    //     ) : null
    //   ) : (
    //     <LandingPage />
    //   ),
    // },
    {
      path: "/",
      element: <HomeRedirect />,
    },
    /**
     * Stripe route starts
     */

    // {
    //   path: "/checkout",
    //   loader: authLoader,
    //   element: <CheckoutPage />,
    // },
    // {
    //   path: "/subscription-plans",
    //   loader: authLoader,
    //   element: <SubscriptionPlanPage />,
    // },
    {
      path: "/",
    },
    {
      path: "/payment-method",
      loader: authLoader,
      element: <PaymentMethodPage />,
    },
    {
      path: "/recruiter-checkout",
      loader: authLoader,
      element: (
        <RecruiterCheckoutPage onPaymentSuccess={() => {}} currentJobId="" />
      ),
    },
    {
      path: "/messages",
      loader: authLoader,
      element: <MessagePage />,
    },
    {
      path: "/balance",
      loader: authLoader,
      element: <BalancePage />,
    },
    {
      path: "/notifications",
      loader: authLoader,
      element: <NotificationPage />,
    },

    /**
     * Stripe route ends
     */
    // Login routes
    {
      path: "/login",
      loader: loginLoader,
      element: <LoginPage />,
    },
    {
      path: "/login-recruiter",
      loader: loginLoader,
      element: <RecruiterLoginPage />,
    },
    {
      path: "/login-company",
      loader: loginLoader,
      element: <HiringCompanyLoginPage />,
    },
    {
      path: "/login-ambassador",
      loader: loginLoader,
      element: <LoginAmbassadorPage />,
    },
    // Register routes
    {
      path: "/register",
      loader: loginLoader,
      element: <RegisterPage />,
    },
    {
      path: "/register/:path",
      loader: loginLoader,
      element: <RegisterPage />,
    },
    {
      path: "/register-ambassador",
      loader: loginLoader,
      element: <RegisterAmbassadorPage />,
    },
    {
      path: "/register-hiring",
      loader: loginLoader,
      element: <HiringCompanyRegisterPage />,
    },
    {
      path: "/register-recruiter",
      loader: loginLoader,
      element: <RecruiterRegisterPage />,
    },
    {
      path: "/sso-login",
      loader: loginLoader,
      element: <SsoLogin />,
    },
    {
      path: "/auth/microsoft/callback",
      loader: loginLoader,
      element: <MicrosoftCallback />,
    },
    {
      path: "/email-verification",
      loader: loginLoader,
      element: <CreateBasicProfileLayout />,
    },
    {
      path: "/forgot-password",
      loader: loginLoader,
      element: <ForgotPassword />,
    },
    {
      path: "/update-forgot-password",
      loader: loginLoader,
      element: <UpdateForgotPassword />,
    },
    {
      path: "/generate-user-name",
      loader: authLoader,
      element: <GenerateUserNamePage />,
    },
    {
      path: "/add-referral",
      loader: loginLoader,
      element: <AddReferralPage />,
    },
    {
      path: "/verify-job/:jobId/:email",
      element: <VerifyUserJobPage />,
    },
    {
      path: "/create-basic-profile",
      loader: authLoader,
      element:
        auth?.user?.userType === EnumUserTypes.RECRUITER ? (
          <RecruiterCreateProfilePage />
        ) : (
          // <CreateProfile/>
          // <CreateBasicProfile />
          <CreateBasicProfileLayout />
        ),
    },
    {
      path: "/create-profile",
      loader: authLoader,
      element: <CreateProfileNew />,
    },
    /**
     * Transaction
     *
     */
    {
      path: "/transactions",
      loader: authLoader,
      element: <TransactionPage />,
    },
    {
      path: "/transactions/details/:id/:type",
      loader: authLoader,
      element: <TransactionDetailPage />,
    },
    {
      path: "/transactions/user-details/:userId/:jobId",
      loader: authLoader,
      element: <TransactionUserDetailPage />,
    },
    {
      path: "/matches",
      loader: authLoader,
      children: [
        {
          index: true,
          element: <MatchesPage />,
        },
        {
          path: ":candidateId/:jobId",
          loader: authLoader,
          element: <MatchesDetailsPage />,
        },
      ],
    },
    {
      path: "/matches-2",
      loader: authLoader,
      children: [
        {
          index: true,
          element: <Matches2Page />,
        },
        {
          path: "match-detail",
          loader: authLoader,
          element: <MatchDetailPage />,
        },
      ],
    },
    {
      path: "/unauthorized",
      loader: authLoader,
      element: <UnauthorizedPage />,
    },
    {
      path: "/search",
      loader: authLoader,
      element: <SearchLayout />,
      children: [
        {
          index: true,
          element: <SearchPage />,
        },
        {
          path: "possible-match",
          element: <PossibleMatchPage />,
        },
        {
          path: "template-result",
          element: <TemplateResultPage />,
        },
        {
          path: "template-searches",
          element: <TemplateSearchesPage />,
        },
        {
          path: "requested-details",
          element: <RequestedDetailsPage />,
        },
      ],
    },
    {
      path: "/search/match-detail",
      element: <DetailPage />,
    },
    {
      path: "/search/search-user-profile",
      element: <NetworkDetailPage />,
    },

    {
      path: "/my-assessments",
      loader: authLoader,
      children: [
        {
          index: true,
          element: <MyAssessmentsPage />,
        },
        {
          path: "assessment-details/:id",
          element: <MyAssessmentsDetailsPage />,
        },
        {
          path: "create-assessment",
          element: <CreateAssessment />,
        },
        {
          path: "edit-assessment/:assessmentId",
          element: <CreateAssessment />,
        },
        {
          path: "assessment-details",
          element: <AssessmentListDetails />,
        },
        {
          path: "assessment-view/:assessmentId",
          element: <AssessmentView />,
        },
        {
          path: "candidate-Assessments",
          element: <CandidateAssessments />,
        },
        {
          path: "candidate-Assessments/:id",
          element: <AnswerAssessment />,
        },
      ],
    },
    // {
    //   path: "/profile",
    //   loader: authLoader,
    //   children: [
    //     {
    //       index: true,
    //       element: <AgentProfile />,
    //     },
    //   ],
    // },
    {
      path: "/view-profile",
      loader: authLoader,
      children: [
        {
          index: true,
          element: <ViewAgentProfile />,
        },
      ],
    },
    {
      path: "/meeting-poll-details",
      element: <MeetingDetailPage />,
    },
    {
      path: "/meeting-details",
      element: <MeetingDetailPage />,
    },
    {
      path: "/counter-proposal-poll",
      element: <CounterProposalPollPage />,
    },
    {
      path: "/job-search",
      children: [
        {
          index: true,
          loader: authLoader,
          element: <JobsListPage />,
        },
        {
          path: ":id",
          loader: authLoader,
          element: <JobApplyPage />,
        },
      ],
    },
    {
      path: "job-detail/:jobId",
      element: <PublicJobDetailPage />,
    },
    {
      loader: authLoader,
      path: "job-applicant-detail",
      element: <JobApplicantDetail />,
    },
    {
      loader: authLoader,
      path: "/add-referral-account",
      element: <AddReferralAccount />,
    },
    {
      loader: authLoader,
      path: "/account/account-members/:id",
      element: <AccountMembersDetail />,
    },
    {
      path: "/my-profile",
      loader: authLoader,
      element: <Profile />,
    },
    {
      path: "/my-profile-original",
      loader: authLoader,
      element: <ProfileOriginal />,
    },
    {
      path: "/my-profile2",
      loader: authLoader,
      element: <ProfilePage2 />,
    },
    {
      path: "/my-profile3",
      loader: authLoader,
      element: <ProfilePage3 />,
    },
    {
      path: "/profile/:userName",
      element: <PublicProfilePage />,
    },
    {
      path: "/change-password",
      loader: authLoader,
      element: <ChangePasswordPage />,
    },
    // {
    //   path: "/job-matches",
    //   loader: authLoader,
    //   children: [
    //     {
    //       index: true,
    //       element: <JobMatchesPage />,
    //     },
    //     {
    //       path: "job-matches-details",
    //       element: <JobMatchedDetailPage />,
    //     },
    //   ],
    // },
    {
      path: "/job-application-detail",
      loader: authLoader,
      element: <JobApplicationDetail />,
    },

    // {
    //   path: "/messages",
    //   loader: authLoader,
    //   element: <MembersProfilePage />,
    //   children: [
    //     {
    //       path: ":jobId",
    //       loader: authLoader,
    //       element: <MessageView />,
    //     },
    //   ],
    // },
    {
      path: "/referral-member",
      loader: authLoader,
      element: <ReferralMemberPage />,
    },
    {
      path: "*",
      loader: authLoader,
      element: <DashboardPage />,
    },
    {
      path: "/settings",
      loader: authLoader,
      element: <SettingsPage />,
      children: [
        {
          path: "/settings/privacy",
          element: <Privacy />,
        },
      ],
    },
  ];

  if (isHiringCompany) {
    layoutChildren.push(...HiringCompanyRoutes);
  }

  if (isRecruiter) {
    layoutChildren.push(...RecruiterRoutes);
  }

  if (isCandidate) {
    layoutChildren.push(...CandidateRoutes);
  }

  if (isReferrealAmbassador) {
    layoutChildren.push(...ReferralAmbassadorRoutes);
  } else {
    layoutChildren.push({
      path: "/account",
      loader: authLoader,
      children: [
        {
          path: "",
          element: <AccountPage />,
          children: [
            {
              path: "/account/account-members",
              element: <AccountMembers />,
            },
            {
              path: "/account/my-company-details",
              element: <MyCompanyDetailsPage />,
            },
            {
              path: "/account/my-account-details",
              element: <MyAccountDetailsPage />,
            },
          ],
        },
      ],
    });
  }

  const feedSearchData = async () => {
    const searchData = JSON.parse(localStorage.getItem("searchData"));
    dispatch(setSearchData({ ...searchData }));
  };

  const feedPaymentMethods = async () => {
    if (
      [userTypes.RECRUITER, userTypes?.REFERRAL_AMBASSADOR]?.includes(
        auth?.user?.userType,
      )
    ) {
      getPaymentMethods("card")
        .then((response) => {
          if (response.success) {
            dispatch(setPaymentMethods({ cards: response?.data || [] }));
          }
        })
        .catch((error) => {});
      getPaymentMethods("bacs_debit")
        .then((response) => {
          if (response.success) {
            dispatch(setPaymentMethods({ banks: response?.data || [] }));
          }
        })
        .catch((error) => {});
    }
  };

  useEffect(() => {
    if (isLoggedIn && auth?.user?.token) {
      feedSearchData();
      feedPaymentMethods();
      if (auth?.user?.userType !== userTypes.HIRING_COMPANY) {
        dispatch(fetchJobMessagesProfile());
        dispatch(fetchUnseenMessageCount());
      }
    }
  }, [isLoggedIn, auth?.user?.token]);

  const router = createBrowserRouter([
    {
      path: "/",
      element: <Layout />,
      children: layoutChildren,
    },
  ]);

  console.log("This is user details", auth);

  return {
    router,
  };
}
export default useRoutes;
